import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  Separator,
} from 'platform/components';
import {Grid, VStack} from 'platform/foundation';
import {z} from 'zod';

import {
  TenantFieldDefinitionResponseBody,
  useUpdateFieldDefinitionMutation,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

interface BaseDefinitionUpdateFormProps extends RequiredTestIdProps {
  definition: TenantFieldDefinitionResponseBody;
}

const schema = z.object({
  name: z.string().nonempty(),
  hint: z.string().nonempty(),
  isRequired: z.boolean(),
  isNotified: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

export function BaseDefinitionUpdateForm(props: BaseDefinitionUpdateFormProps) {
  const [updateFieldDefinition] = useUpdateFieldDefinitionMutation();

  const handleSubmit: FormSubmitHandler<FormValues> = async (values) => {
    await updateFieldDefinition({
      resourceId: props.definition.resource.id,
      definitionId: props.definition.id,
      body: {
        name: values.name,
        hint: values.hint,
        isRequired: values.isRequired,
        isNotified: values.isNotified,
      },
    })
      .unwrap()
      .then(() => {
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  const defaultValues: FormValues = {
    name: props.definition.name,
    hint: props.definition.hint,
    isRequired: props.definition.isRequired ?? false,
    isNotified: props.definition.isNotified ?? false,
  };

  return (
    <Form<FormValues>
      onSubmit={handleSubmit}
      experimentalZodSchema={schema}
      defaultValues={defaultValues}
    >
      {(control) => {
        const selectedValueType = props.definition.valueType;

        return (
          <VStack spacing={4}>
            <Grid columns={3}>
              <FormField
                type="checkbox"
                control={control}
                name="isRequired"
                label={i18n.t('page.generalSettings.labels.isFieldRequired')}
                data-testid={suffixTestId('isRequired', props)}
              />

              {['date', 'datetime'].includes(selectedValueType) && (
                <FormField
                  type="checkbox"
                  control={control}
                  name="isNotified"
                  label={i18n.t('page.generalSettings.labels.isNotified')}
                  data-testid={suffixTestId('isNotified', props)}
                />
              )}
            </Grid>
            <Separator spacing={0} />
            <Grid columns={2}>
              <FormField
                type="text"
                control={control}
                name="name"
                label={i18n.t('page.generalSettings.labels.name')}
                data-testid={suffixTestId('name', props)}
                isRequired
              />

              <FormField
                type="text"
                control={control}
                name="hint"
                label={i18n.t('page.generalSettings.labels.hint')}
                data-testid={suffixTestId('hint', props)}
                isRequired
              />
            </Grid>

            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.discardChanges')}
                variant="secondary"
                onClick={closeCurrentDialog}
                data-testid={suffixTestId('discardChanges', props)}
              />
              <FormButton
                leftIcon="content/save"
                title={i18n.t('general.actions.saveChanges')}
                control={control}
                data-testid={suffixTestId('save', props)}
                type="submit"
              />
            </ButtonGroup>
          </VStack>
        );
      }}
    </Form>
  );
}
